import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { getSalesSummaryGranularityOptionsForRange } from '@/lib/services/reports/reports.schema'
import type {
  SalesSummaryReportGranularity,
  SalesSummaryReportTimeSeriesBucket,
} from '@/lib/services/reports/reports.types'
import { formatCurrency } from '@/lib/utils'
import { formatCurrencyAxis } from './-helpers'
import { SalesPerformanceChart } from './-area-trend-chart'
import { SectionCardHeader } from './-section-card-header'

type SalesSummaryTrendCardProps = {
  data: SalesSummaryReportTimeSeriesBucket[]
  granularity: SalesSummaryReportGranularity
  granularityOptions?: ReturnType<typeof getSalesSummaryGranularityOptionsForRange>
  timezone: string
  onGranularityChange?: (granularity: string) => void
  isGlobalReport?: boolean
  isOrderBased?: boolean
}

export function SalesSummaryTrendCard({
  data,
  granularity,
  granularityOptions = [],
  timezone,
  onGranularityChange,
  isGlobalReport = false,
  isOrderBased = false,
}: SalesSummaryTrendCardProps) {
  const bucketLabel = getBucketLabel(granularity)
  const bucketedLabel = `${bucketLabel} ${isGlobalReport || isOrderBased ? 'Sales' : 'Order Payments'}`

  return (
    <Card className="flex min-h-[420px] flex-col xl:h-[580px]">
      <SectionCardHeader
        title="Performance Over Time"
        description={
          isGlobalReport
            ? `Total sales to date with ${bucketLabel.toLowerCase()} sales underneath`
            : isOrderBased
              ? undefined
              : 'Order payments over time'
        }
        action={
          onGranularityChange ? (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-muted-foreground">Group by</span>
              <Select value={granularity} onValueChange={onGranularityChange}>
                <SelectTrigger size="toolbar" className="w-[112px]" aria-label="Group by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent align="end">
                  {granularityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
                      <span className="flex flex-col items-start">
                        <span>{option.label}</span>
                        {option.disabled && option.unavailableLabel ? (
                          <span className="text-xs text-muted-foreground">
                            Up to {option.unavailableLabel}
                          </span>
                        ) : null}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : null
        }
      />
      <CardContent className="flex min-h-0 flex-1">
        <SalesPerformanceChart
          data={data}
          dataKey="total_sales"
          bucketedLabel={bucketedLabel}
          cumulativeLabel="Total to Date"
          valueFormatter={(value) => formatCurrency(value)}
          axisFormatter={formatCurrencyAxis}
          granularity={granularity}
          timezone={timezone}
        />
      </CardContent>
    </Card>
  )
}

function getBucketLabel(granularity: SalesSummaryReportGranularity) {
  switch (granularity) {
    case 'hourly':
      return 'Hourly'
    case 'daily':
      return 'Daily'
    case 'weekly':
      return 'Weekly'
    case 'monthly':
      return 'Monthly'
    default:
      return 'Period'
  }
}
